import { useCallback } from "react";

import {
  ChevronDownIcon,
  ChevronUpIcon,
  PlusIcon,
  TrashIcon,
} from "@vericus/cadmus-icons";
import { Button, Checkbox, LinkButton, Text, vars } from "@vericus/cadmus-ui";

import { show } from "@ebay/nice-modal-react";
import { flexRender } from "@tanstack/react-table";
import clsx from "clsx";

import rubricImg from "@/assets/illustrations/rubric.png";
import { ApiError } from "@/components/errors";
import { LoadingPage } from "@/components/loading-page";
import {
  AssessmentSettingsFragment,
  GroupFragment,
  IssueStatus,
  IssueType,
  ProjectIssueFragment,
  ProjectIssueInput,
  useProjectIssuesQuery,
  UserFragment,
} from "@/generated/graphql";

import { getActiveMembers } from "../group/utils";
import { IssueFormModal } from "../project-management/components/issue-form";
import {
  ProjectManagementTableContext,
  useProjectManagementTableContext,
} from "../project-management/components/table/context";
import {
  IssueAssigneeFilter,
  IssueDateRangeFilter,
  IssueStatusFilter,
} from "../project-management/components/table/issue-filters";
import { ProjectIssuesTable } from "../project-management/hooks/use-project-issues-table";
import { getStatusBadgeStyles } from "../project-management/utils";
import { StatusCell } from "./components/issue";
import { IssueMilestoneFilter } from "./components/table/issue-filters/issue-milestone-filter";
import { LocalProjectIssueFormState } from "./hooks/use-issue-form";
import { useProjectManagementBoard } from "./hooks/use-project-management-board";
import {
  IssueSelectionState,
  useIssueSelection,
} from "./hooks/use-task-selection";
import { useUpdateProjectIssue } from "./hooks/use-update-project-issue";
import * as styles from "./project-management-board.css";
import { ProjectIssueWithChildren } from "./types";

export function ProjectManagementBoardWithData({
  group,
  assessmentSettings,
  charterAvailable,
}: {
  group: GroupFragment;
  assessmentSettings: AssessmentSettingsFragment;
  /**
   * Charter is usable: enabled in settings AND has a published charter form
   * (the server-derived `groupCharterAvailable`). Milestones come from the
   * charter, so without one the board is tasks-only.
   */
  charterAvailable?: boolean;
}) {
  const { data, loading, error } = useProjectIssuesQuery({
    variables: { groupId: group.id },
  });

  if (loading) return <LoadingPage />;
  if (error || !data) return <ApiError />;

  return (
    <ProjectManagementBoard
      group={group}
      issues={data.projectIssues}
      isShowTasksOnly={
        !(charterAvailable ?? assessmentSettings.groupCharterAvailable)
      }
      assessmentDueDate={new Date(assessmentSettings.dueDate ?? "")}
    />
  );
}

interface ProjectManagementBoardProps {
  /**
   * Group that is undergoing the Project Management workflow.
   */
  group: GroupFragment;

  /**
   * Project issues that are from this Group in this
   * Group Assessment. They mainly consist of Milestone and/or Task
   * Types. Milestones would be CRUD-ed from the GC page, whereas
   * in this Project Management Page, we can only CRUD Tasks either
   * under the milestones or orphaned.
   */
  issues: ProjectIssueFragment[];

  /**
   * Depending on whether Group Charter (GC) is enabled or not,
   * we render the PM board differently as non-GC enabled assessments
   * do not have milestones.
   */
  isShowTasksOnly: boolean;

  /**
   * The calendars used in the PM board should consider its possible
   * selectable maximum Date as the assessment's due date. The students should
   * not be dealing with dates past the assessment's due.
   */
  assessmentDueDate: Date;
}

export function ProjectManagementBoard({
  group,
  issues,
  isShowTasksOnly,
  assessmentDueDate,
}: ProjectManagementBoardProps) {
  const { table, totalTasksCount } = useProjectManagementBoard(
    issues,
    isShowTasksOnly
  );
  const issueSelection = useIssueSelection();
  const updateProjectIssue = useUpdateProjectIssue(group.id);

  const members = getActiveMembers(group);

  const memberOptions = members.map((m) => {
    return { value: m.id, label: m.name };
  });

  const milestoneOptions = !isShowTasksOnly
    ? table
        .getRowModel()
        .rows.filter((issue) => issue.original.type === IssueType.Milestone)
        .map((milestone) => {
          return {
            value: milestone.original.id,
            text: milestone.original.title,
          };
        })
    : [];

  return (
    <ProjectManagementTableContext.Provider
      value={{
        group,
        table,
        isShowTasksOnly,
        assessmentDueDate,
      }}
    >
      <ProjectIssueTableSkeleton
        totalTasksCount={totalTasksCount}
        issueSelection={issueSelection}
        onCreateNewIssue={() => {
          show(IssueFormModal, {
            currentIssue: undefined,
            currentIssueParent: undefined,
            onSaveIssue: updateProjectIssue,
            milestoneOptionsEnabled: !isShowTasksOnly,
            milestoneOptions,
            memberOptions,
            assessmentDueDate,
            table,
          });
        }}
        onUpdateIssue={updateProjectIssue}
      />
    </ProjectManagementTableContext.Provider>
  );
}

interface ProjectIssueTableSkeletonProps {
  totalTasksCount: number;
  issueSelection: IssueSelectionState;
  onCreateNewIssue: () => void;
  onUpdateIssue: (issue: ProjectIssueInput[]) => void;
}

function ProjectIssueTableSkeleton({
  issueSelection,
  onCreateNewIssue,
  onUpdateIssue,
  totalTasksCount,
}: ProjectIssueTableSkeletonProps) {
  const { group, table, isShowTasksOnly, assessmentDueDate } =
    useProjectManagementTableContext();

  const issues = table.getRowModel().rows;

  const renderEl = () => {
    // GC enabled and show milestones
    if (!isShowTasksOnly && issues.length > 0)
      return issues.map((milestone) => (
        <MilestoneCard
          key={milestone.id}
          table={table}
          milestoneId={milestone.id}
          onShowIssueForm={onCreateNewIssue}
          issueSelection={issueSelection}
        />
      ));

    // GC enabled but no milestones to show
    if (!isShowTasksOnly && issues.length === 0)
      return <EmptyState title="milestones" onCreateClick={onCreateNewIssue} />;

    // GC disabled and show tasks
    if (isShowTasksOnly && issues.length > 0)
      return (
        <div style={{ overflowX: "auto", width: "100%" }}>
          <IssuesTable
            issues={issues.map((t) => t.original)}
            table={table}
            issueSelection={issueSelection}
          />
        </div>
      );

    // GC disabled and no tasks to show
    if (isShowTasksOnly && issues.length === 0)
      return <EmptyState title="tasks" onCreateClick={onCreateNewIssue} />;
  };

  return (
    <div className={styles.taskBoardCard}>
      <div className={styles.taskBoardSection}>
        <div className={styles.taskBoardUtilityBar}>
          <IssuesFilterBar
            table={table}
            assignees={getActiveMembers(group)}
            assessmentDueDate={assessmentDueDate}
            isShowTasksOnly={isShowTasksOnly}
          />
          <ActionButton
            table={table}
            issueSelection={issueSelection}
            onUpdateProjectIssue={onUpdateIssue}
            onCreateNewIssue={onCreateNewIssue}
          />
        </div>
      </div>

      <TaskCountDisplay
        tasksCount={totalTasksCount}
        selectedCount={issueSelection.selectedCount}
      />

      <div className={styles.taskBoardSection}>{renderEl()}</div>
    </div>
  );
}

interface IssuesTableProps {
  issues: ProjectIssueWithChildren[];
  table: ProjectIssuesTable;
  issueSelection: IssueSelectionState;
  showCheckbox?: boolean;
}

function IssuesTable({ issues, table, issueSelection }: IssuesTableProps) {
  const allSelected = issueSelection.areAllIssuesSelected(issues);
  const someSelected = issueSelection.areSomeIssuesSelected(issues);

  const handleToggleAll = useCallback(() => {
    issueSelection.onToggleAllIssues(issues);
  }, [issues, issueSelection]);

  return (
    <div
      style={{
        backgroundColor: vars.colors.white,
        minWidth: 1330,
        maxWidth: 1330,
        overflowX: "auto",
        borderRadius: "8px",
        padding: 12,
      }}
    >
      <div className={clsx(styles.taskRow, styles.headerRow)}>
        <div style={{ width: 20 }}>
          <Checkbox
            checked={allSelected}
            mixed={someSelected}
            onChange={handleToggleAll}
            aria-label="Select all tasks"
          />
        </div>
        <Text kind="headingSix" as="div" style={{ width: 220 }} bold>
          Task
        </Text>
        <Text kind="headingSix" as="div" bold>
          Notes
        </Text>
        <Text kind="headingSix" as="div" style={{ width: 230 }} bold>
          Assignee
        </Text>
        <Text kind="headingSix" as="div" style={{ width: 200 }} bold>
          Due Date
        </Text>
        <Text kind="headingSix" as="div" style={{ width: 130 }} bold>
          Status
        </Text>
        <div style={{ width: 40 }} />
      </div>

      {issues.map((issue) => (
        <IssueRow
          key={issue.id}
          issue={issue}
          table={table}
          isSelected={issueSelection.isIssueSelected(issue.id)}
          onIssueSelection={issueSelection.onIssueSelection}
        />
      ))}
    </div>
  );
}

function MilestoneCard({
  table,
  milestoneId,
  issueSelection,
  onShowIssueForm,
}: {
  milestoneId: string;
  table: ProjectIssuesTable;
  onShowIssueForm: () => void;
  issueSelection: IssueSelectionState;
}) {
  const milestoneRow = table
    .getCoreRowModel()
    .rows.find((r) => r.original.id === milestoneId);

  const milestone = milestoneRow?.original ?? null;
  const isExpanded = milestoneRow?.getIsExpanded() ?? false;
  const childTasks = milestoneRow?.original.children || [];

  if (!milestone) return null;

  const renderContent = () => {
    if (!isExpanded) return null;

    // show tasks under the mielstone
    if (isExpanded && childTasks.length > 0)
      return (
        <div className={styles.tasksSection}>
          <IssuesTable
            issues={childTasks}
            table={table}
            issueSelection={issueSelection}
          />
        </div>
      );

    // no tasks to show under the milestone
    if (isExpanded && childTasks.length === 0)
      return <EmptyState title="tasks" onCreateClick={onShowIssueForm} />;
  };

  return (
    <div className={styles.milestoneCard}>
      <MilestoneHeader
        milestone={milestone}
        isExpanded={isExpanded}
        onToggleExpanded={() => milestoneRow?.toggleExpanded()}
      />
      {renderContent()}
    </div>
  );
}

function MilestoneHeader({
  milestone,
  isExpanded,
  onToggleExpanded,
}: {
  milestone: ProjectIssueFragment;
  isExpanded: boolean;
  onToggleExpanded: () => void;
}) {
  return (
    <div className={styles.milestoneRow}>
      <div className={styles.milestoneHeaderLeft}>
        <div className={styles.milestoneTitleDate}>
          <Text kind="bodyLine" as="div" className={styles.milestoneTitle}>
            {milestone.title}
          </Text>

          <Text kind="bodyLine" as="div" className={styles.milestoneDueDate}>
            {milestone.dueDate
              ? new Date(milestone.dueDate).toDateString()
              : "No due date"}
          </Text>
        </div>

        <div
          className={clsx(
            styles.statusBadge,
            getStatusBadgeStyles(milestone.status)
          )}
        >
          <StatusCell status={milestone.status} />
        </div>
      </div>

      <div className={styles.milestoneHeaderRight}>
        {isExpanded ? (
          <ChevronUpIcon
            className={styles.expandButton}
            onClick={onToggleExpanded}
            label="collapse"
            size={24}
          />
        ) : (
          <ChevronDownIcon
            className={styles.expandButton}
            onClick={onToggleExpanded}
            label="expand"
            size={24}
          />
        )}
      </div>
    </div>
  );
}

function EmptyState({
  title,
  onCreateClick,
}: {
  title: string;
  onCreateClick: () => void;
}) {
  return (
    <div className={styles.emptyState}>
      <Text kind="headingFour" style={{ marginBottom: "1rem" }}>
        No {title}
      </Text>
      {title === "tasks" && (
        <LinkButton
          icon={<PlusIcon label="create new" size={24} />}
          kind="solid"
          onClick={onCreateClick}
        >
          Create new task
        </LinkButton>
      )}
      {title === "milestones" && (
        <Text kind="bodyLine">
          Visit the Group Charter to begin creating milestones for your group.
        </Text>
      )}
    </div>
  );
}

function IssueRow({
  issue,
  table,
  isSelected,
  onIssueSelection,
}: {
  issue: ProjectIssueWithChildren;
  table: ProjectIssuesTable;
  isSelected: boolean;
  onIssueSelection: (taskId: string, isSelected: boolean) => void;
}) {
  const issueRow = table
    .getRowModel()
    .rows.find((row) => row.original.id === issue.id);

  if (!issueRow) return null;

  return (
    <div className={styles.taskRow}>
      <div>
        <Checkbox
          checked={isSelected}
          onChange={() => onIssueSelection(issue.id, !isSelected)}
          aria-label={`Select task ${issue.title}`}
        />
      </div>
      {issueRow
        .getVisibleCells()
        .slice(1)
        .map((cell) => (
          <div key={cell.id} style={{ width: "100%" }}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </div>
        ))}
    </div>
  );
}

export function EmptyProjectManagementBoard() {
  return (
    <div className={styles.root}>
      <div className={styles.taskContent}>
        <img src={rubricImg} height={280} width={280} alt="" />
        <Text kind="headingThree" textAlign="center">
          You&apos;ll see your group&apos;s tasks here once they are created
        </Text>
        <Text kind="bodyLine" textAlign="center">
          Manage your group work by adding tasks, assigning roles, and tracking
          progress. Collaborate efficiently to ensure everyone contributes to
          the project plan.
        </Text>
      </div>
    </div>
  );
}

export function ChaterUnacceptedProjectManagementBoard() {
  return (
    <div className={styles.root}>
      <div className={styles.taskContent}>
        <img src={rubricImg} height={280} width={280} alt="" />
        <Text kind="headingThree" textAlign="center">
          You&apos;ll see your group&apos;s tasks here once you accept the group
          charter
        </Text>
        <Text kind="bodyLine" textAlign="center">
          After accepting the group charter, manage your group work by adding
          tasks, assigning roles, and tracking progress. Collaborate efficiently
          to ensure everyone contributes to the project plan.
        </Text>
      </div>
    </div>
  );
}

function IssuesFilterBar({
  table,
  assignees,
  assessmentDueDate,
  isShowTasksOnly,
}: {
  table: ProjectIssuesTable;
  assignees: UserFragment[];
  assessmentDueDate: Date;
  isShowTasksOnly: boolean;
}) {
  return (
    <div className={styles.filterBar}>
      <IssueDateRangeFilter table={table} maxDate={assessmentDueDate} />
      {!isShowTasksOnly && <IssueMilestoneFilter table={table} />}
      <IssueAssigneeFilter table={table} assignees={assignees} />
      <IssueStatusFilter table={table} />
    </div>
  );
}

function TaskCountDisplay({
  tasksCount,
  selectedCount,
}: {
  tasksCount: number;
  selectedCount: number;
}) {
  if (selectedCount > 0) {
    return (
      <Text as="div" kind="headingSix" style={{ padding: "10px" }}>
        {selectedCount} Task{selectedCount > 1 ? "s" : ""} Selected
      </Text>
    );
  }

  return (
    <Text as="div" kind="headingSix" style={{ padding: "10px" }}>
      {tasksCount} Task{tasksCount > 1 ? "s" : ""}
    </Text>
  );
}

function ActionButton({
  issueSelection,
  onUpdateProjectIssue,
  onCreateNewIssue,
}: {
  table: ProjectIssuesTable;
  issueSelection: IssueSelectionState;
  onUpdateProjectIssue: (issue: ProjectIssueInput[]) => void;
  onCreateNewIssue: () => void;
}) {
  const issuesToDelete = Array.from(issueSelection.selectedIssueIds).map(
    (tid) => {
      return { id: tid, deleted: true };
    }
  ) as ProjectIssueInput[];

  return (
    <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
      {issueSelection.selectedCount > 0 ? (
        <>
          <Button kind="ghost" onClick={issueSelection.onClearSelection}>
            Clear Selection
          </Button>
          <Button
            kind="error"
            icon={<TrashIcon label="Delete selected tasks" />}
            onClick={() => {
              onUpdateProjectIssue(issuesToDelete);
              issueSelection.onClearSelection();
            }}
          >
            Delete {issueSelection.selectedCount} Task
            {issueSelection.selectedCount > 1 ? "s" : ""}
          </Button>
        </>
      ) : (
        <Button
          kind="primary"
          icon={<PlusIcon label="Create new task" />}
          onClick={onCreateNewIssue}
        >
          New Task
        </Button>
      )}
    </div>
  );
}

export function getProjectIssueInput(
  projectIssue: LocalProjectIssueFormState
): ProjectIssueInput {
  return {
    id: projectIssue.id ?? null,
    assignees: (projectIssue.assignees ?? []).map((id) => {
      return { userId: id };
    }),
    deleted: projectIssue.deleted ?? false,
    description: projectIssue.description ?? null,
    dueDate: projectIssue.dueDate ?? null,
    parentId: projectIssue.parentId ?? null,
    status: projectIssue.status ?? IssueStatus.NotStarted,
    title: projectIssue.title ?? "Unnamed Task",
    type: projectIssue.type ?? IssueType.Task,
  };
}
